import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight, ClipboardCheck } from "lucide-react";
import { Reveal, Chapter } from "@/components/Reveal";
import { CHECKLIST, WHY_POINTS, APPLY_PATH } from "@/data/content";

export const Requirements = () => {
  const [checked, setChecked] = useState(() => new Set());
  const toggle = (i) => {
    setChecked((prev) => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });
  };
  const score = Math.round((checked.size / CHECKLIST.length) * 100);

  return (
    <section id="requirements" className="relative py-24 lg:py-32" data-testid="requirements-section">
      <div className="container-x grid items-start gap-12 lg:grid-cols-[1.2fr_1fr]">
        <Reveal>
          <Chapter index="05" label="Partner Requirements" />
          <h2 className="font-display text-2xl font-bold tracking-tight text-offwhite sm:text-3xl lg:text-4xl">
            Is Your Business a <span className="text-aqua">Strong Fit?</span>
          </h2>
          <p className="mt-4 max-w-xl text-base leading-relaxed text-muted">
            Check the criteria that describe your operation today. This self-assessment is informal — the official
            review happens after you apply.
          </p>
          <div className="mt-9 grid gap-2.5 sm:grid-cols-2">
            {CHECKLIST.map((item, i) => {
              const on = checked.has(i);
              return (
                <button
                  key={item}
                  onClick={() => toggle(i)}
                  aria-pressed={on}
                  data-testid={`qualification-checkbox-${i}`}
                  className={`flex items-start gap-3 rounded-lg border p-4 text-left text-sm leading-snug transition-[border-color,background-color] duration-300 ${
                    on ? "border-aqua/50 bg-aqua/[0.08] text-offwhite" : "border-black/[0.08] bg-charcoal/40 text-muted hover:border-black/20"
                  }`}
                >
                  <span
                    className={`mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded border transition-colors duration-300 ${
                      on ? "border-aqua bg-aqua text-[#052726]" : "border-black/25 text-transparent"
                    }`}
                  >
                    <Check size={13} strokeWidth={3} />
                  </span>
                  {item}
                </button>
              );
            })}
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="card-line sticky top-28 rounded-2xl p-8" data-testid="readiness-panel">
            <p className="mb-2 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-muted">
              <ClipboardCheck size={14} className="text-aqua" /> Informal readiness check
            </p>
            <p className="font-display text-6xl font-extrabold text-offwhite" data-testid="readiness-score">
              {score}<span className="text-2xl text-aqua">%</span>
            </p>
            <div className="mt-4 h-1.5 overflow-hidden rounded-full bg-black/[0.07]">
              <div
                className="h-full rounded-full bg-aqua transition-[width] duration-500"
                style={{ width: `${score}%` }}
              />
            </div>
            <p className="mt-5 text-sm leading-relaxed text-muted">
              {score === 100
                ? "Your operation checks every box. The next step is the official qualification review."
                : score >= 60
                  ? "You meet several core criteria. Apply and our team will review the full picture."
                  : "Every operation is different. If you have a strong waterfront location, we still want to hear from you."}
            </p>
            <div className="mt-7 flex flex-col gap-3">
              <Link to={APPLY_PATH} className="btn-primary w-full" data-testid="check-eligibility-btn">
                Check Your Eligibility <ArrowRight size={16} />
              </Link>
              <Link to={APPLY_PATH} className="btn-ghost w-full" data-testid="start-application-btn">
                Start Your Application
              </Link>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export const WhyPartner = () => (
  <section className="noise relative overflow-hidden bg-charcoal py-24 lg:py-32" data-testid="why-partner-section">
    <div className="spotlight absolute inset-0" />
    <div className="container-x relative">
      <Reveal>
        <Chapter index="07" label="Why Xtreme Exotics" />
        <h2 className="max-w-3xl font-display text-2xl font-bold tracking-tight text-offwhite sm:text-3xl lg:text-4xl">
          Why Partner With <span className="text-aqua">Xtreme Exotics</span>
        </h2>
      </Reveal>
      <div className="mt-14 grid gap-px overflow-hidden rounded-2xl border border-black/[0.07] bg-black/[0.07] sm:grid-cols-2 lg:grid-cols-3">
        {WHY_POINTS.map((p, i) => (
          <Reveal key={p.title} delay={i * 0.06} className="h-full">
            <div className="group flex h-full flex-col bg-charcoal p-8 transition-colors duration-500 hover:bg-surface" data-testid={`why-point-${i}`}>
              <span className="font-mono text-xs text-aqua/70">{String(i + 1).padStart(2, "0")}</span>
              <h3 className="mt-4 font-display text-lg font-bold leading-snug text-offwhite">{p.title}</h3>
              <p className="mt-2.5 text-sm leading-relaxed text-muted">{p.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
